import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { featuresLostOnStart } from '@/lib/card-trial';
import { iconCircleInfo, iconCoins, iconSpinnerThird } from '@/lib/icons';

interface TrialStartNowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planLabel: string;
  /** Tier key of the plan being started, e.g. "pro". */
  tier: string;
  /** Plan price in dollars per `interval`; omitted when the card didn't report one. */
  price?: number;
  /** Exact coupon-aware amount due from the server's Stripe preview. */
  chargeNow?: number;
  interval: 'month' | 'year';
  credits?: number;
  /** "Plus 1 extra seat at $15/month" when the team needs more than the plan's seats. */
  extraSeats?: string;
  /** The add-ons charged with the plan, already in `price`: "Includes 3,500 extra credits". */
  addons?: string;
  busy: boolean;
  onConfirm: () => void;
}

/**
 * Card-backed trial: "Start today" charges the saved card with no Stripe page,
 * so this is the one honest confirmation — what's charged, what's unlocked and
 * what the trial gives up by ending today.
 */
export function TrialStartNowDialog({
  open,
  onOpenChange,
  planLabel,
  tier,
  price,
  chargeNow,
  interval,
  credits,
  extraSeats,
  addons,
  busy,
  onConfirm,
}: TrialStartNowDialogProps) {
  const isV4 = tier.startsWith('v4:');
  const lost = isV4
    ? tier === 'v4:starter'
      ? ['Agents', 'LinkedIn steps', 'Pro integrations']
      : []
    : featuresLostOnStart(tier);
  const amount = price != null ? `$${price.toLocaleString('en-US')}` : null;
  const chargeAmount =
    chargeNow != null ? `$${chargeNow.toLocaleString('en-US')}` : amount;
  const cadence = interval === 'year' ? 'yearly' : 'monthly';
  const rows = [
    {
      label: 'Charged today',
      value: chargeAmount ?? 'Plan price',
      hint: [
        addons,
        extraSeats,
        chargeNow != null
          ? 'Invoice total, including applied discounts and tax'
          : 'Before tax',
      ]
        .filter(Boolean)
        .join(' · '),
    },
    {
      label: 'Credits unlocked now',
      value: credits ? `+${credits.toLocaleString('en-US')}` : 'Plan credits',
      hint: "On top of what's left of your trial credits",
    },
    {
      label: 'Renews',
      hint: isV4 ? 'Before recurring discounts and tax' : undefined,
      value: amount
        ? `${amount} ${cadence} from today`
        : `${cadence} from today`,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[440px] gap-0 overflow-hidden rounded-2xl p-0'>
        <div className='px-7 pt-7'>
          <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'>
            <AppIcon icon={iconCoins} className='h-4 w-4' />
          </span>
          <DialogTitle className='mt-5 text-xl font-semibold tracking-[-0.02em]'>
            Start {planLabel} today?
          </DialogTitle>
          <DialogDescription className='mt-1.5 text-sm leading-6 text-muted-foreground'>
            Your trial ends now and {planLabel} starts on the card you added.
          </DialogDescription>
        </div>

        <dl className='mx-7 mt-5 divide-y divide-border rounded-xl border border-border'>
          {rows.map((row) => (
            <div
              key={row.label}
              className='flex items-start justify-between gap-4 px-4 py-3'
            >
              <dt className='text-sm text-muted-foreground'>
                {row.label}
                {row.hint && (
                  <span className='block text-xs text-muted-foreground/70'>
                    {row.hint}
                  </span>
                )}
              </dt>
              <dd className='text-sm font-semibold text-foreground tabular-nums'>
                {row.value}
              </dd>
            </div>
          ))}
        </dl>

        <div className='mx-7 mt-4 flex gap-2.5 rounded-xl bg-muted/50 px-4 py-3 text-[13px] leading-5 text-muted-foreground'>
          <AppIcon
            icon={iconCircleInfo}
            className='mt-0.5 h-3.5 w-3.5 shrink-0'
          />
          {lost.length ? (
            <span>
              {isV4
                ? 'These Pro features are not included on Starter:'
                : 'Enterprise features end with the trial:'}{' '}
              <span className='text-foreground'>{lost.join(', ')}</span>. Pick{' '}
              {isV4 ? 'Pro' : 'Enterprise'} instead to keep them.
            </span>
          ) : (
            <span>You keep everything from your trial.</span>
          )}
        </div>

        <div className='px-7 pt-6 pb-7'>
          <Button
            variant='brand'
            className='h-11 w-full rounded-xl text-[15px] font-semibold'
            onClick={onConfirm}
            disabled={busy}
          >
            {busy && <AppIcon icon={iconSpinnerThird} spin />}
            Start {planLabel}
            {chargeAmount ? ` · Pay ${chargeAmount}` : ''}
          </Button>
          <button
            type='button'
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className='mt-3 w-full py-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50'
          >
            Not now
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
